import { LegalLayout, Section, Sub, P, UL, Strong, Code } from '../components/LegalLayout.jsx';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      effectiveDate="May 16, 2026"
      lastUpdated="May 16, 2026"
    >
      <P>
        Welcome to Pareto. These Terms of Service (<Strong>Terms</Strong>) are a contract
        between you and <Strong>Pareto Labs Inc</Strong> (<Strong>Pareto</Strong>,{' '}
        <Strong>we</Strong>, <Strong>us</Strong>, or <Strong>our</Strong>) and govern your
        use of our website at <Code>trypareto.ai</Code>, our messaging bots on WhatsApp and
        iMessage, and any related services we offer (together, the <Strong>Service</Strong>).
      </P>
      <P>
        By creating an account, sending a message to our WhatsApp or iMessage bot, or
        otherwise using the Service, you agree to be bound by these Terms. If you do not
        agree, do not use the Service.
      </P>

      <Section number={1} title="What the Service does">
        <P>Pareto is an autonomous, AI-powered career agency. We do three main things for you:</P>
        <UL>
          <li>
            <Strong>Match you to jobs.</Strong> We collect your resume, LinkedIn profile, work
            history, location, and stated preferences, and use AI to match you to relevant open
            positions sourced from public job boards and applicant tracking systems.
          </li>
          <li>
            <Strong>Apply for you.</Strong> With your authorization (see Section 5), we
            automatically submit job applications on your behalf using AI-generated,
            profile-tailored resumes and cover letters. Each application runs in a temporary
            browser environment we operate; we record a screenshot for your reference.
          </li>
          <li>
            <Strong>Connect you to paid research interviews.</Strong> We match you with
            research interview campaigns run by companies and pay you for the time you spend
            on those interviews. Payments are processed through Stripe Connect.
          </li>
        </UL>
        <P>
          Most users interact with the Service primarily through our WhatsApp or iMessage bot.
          The web dashboard at <Code>trypareto.ai</Code> is a complementary control panel; you
          can use the Service entirely through messaging if you prefer.
        </P>
      </Section>

      <Section number={2} title="Eligibility">
        <P>To use the Service you must:</P>
        <UL>
          <li>
            Be at least <Strong>18 years old</Strong>.
          </li>
          <li>Have the legal right to work, or to seek work, in at least one country.</li>
          <li>Provide accurate, current, and complete information about yourself.</li>
          <li>Not be barred from using the Service under applicable laws.</li>
        </UL>
        <P>
          We do not knowingly collect information from anyone under 18. If you believe a minor
          has registered, contact us at <Code>legal@trypareto.ai</Code> and we will delete the
          account.
        </P>
      </Section>

      <Section number={3} title="Your account">
        <P>
          You can create an account by signing up on our web app (with Google or email and
          password), or by messaging our WhatsApp or iMessage bot from a phone number you
          control.
        </P>
        <P>You are responsible for:</P>
        <UL>
          <li>Keeping your login credentials, OAuth tokens, and verification codes confidential.</li>
          <li>All activity that occurs under your account, whether or not authorized by you.</li>
          <li>
            Notifying us immediately at <Code>legal@trypareto.ai</Code> if you believe your
            account has been compromised.
          </li>
        </UL>
        <P>
          You may not create more than one account, share your account with others, or use
          someone else's account.
        </P>
      </Section>

      <Section number={4} title="Accuracy of your profile">
        <P>
          You agree that all profile information you provide — including your name, work
          history, resume content, skills, and answers to screener questions — is accurate,
          current, and yours to share. You may not impersonate any other person, fabricate
          qualifications, or knowingly submit false information.
        </P>
        <P>
          You are solely responsible for the accuracy of the information that we use to apply
          for jobs and respond to research interviews on your behalf.
        </P>
      </Section>

      <Section number={5} title="Auto-Apply authorization (important)">
        <P>
          By enabling the auto-apply feature, you give Pareto{' '}
          <Strong>explicit permission</Strong> to do the following on your behalf,
          automatically and without prior approval of each individual application:
        </P>
        <UL>
          <li>
            Submit job applications to open positions on third-party career sites, employer
            websites, and applicant tracking systems (such as Greenhouse, Lever, Workday,
            iCIMS, Oracle/Taleo, LinkedIn, Indeed, ZipRecruiter, Glassdoor, and similar).
          </li>
          <li>
            Generate and submit AI-tailored resumes and cover letters that draw on your
            profile data.
          </li>
          <li>
            Type, click, and upload files in a browser session that we operate on a serverless
            sandbox.
          </li>
          <li>
            Use a proxy email address that we issue you, so employer replies route into your
            Pareto inbox.
          </li>
          <li>
            If you have provided credentials for sites that require an account (e.g., Workday),
            use those credentials to log in and complete the application.
          </li>
        </UL>
        <P>
          You retain ultimate responsibility for the content of every application submitted
          under your name. If you do not want a specific application submitted, you must
          disable auto-apply or revoke authorization before the application is sent.
        </P>
        <P>
          <Strong>You may revoke this authorization at any time</Strong> by replying "stop" or
          "pause" to our messaging bot, toggling auto-apply off in the web dashboard, or
          contacting <Code>legal@trypareto.ai</Code>.
        </P>
      </Section>

      <Section number={6} title="Daily application limits and fair use">
        <P>The Service is provided subject to fair-use limits:</P>
        <UL>
          <li>
            A base daily auto-apply limit of <Strong>5 applications per day</Strong>.
          </li>
          <li>
            An additional <Strong>+2 daily applications for each successful referral</Strong>{' '}
            you make, up to a hard cap of <Strong>15 per day</Strong>.
          </li>
          <li>
            Paid research interviews are always unlimited and do not count against this cap.
          </li>
        </UL>
        <P>
          We may temporarily lower or pause your limits if we detect abuse, low responsiveness,
          or behavior inconsistent with these Terms.
        </P>
      </Section>

      <Section number={7} title="Paid research interviews">
        <P>
          Through the Service, you may be invited to participate in paid research interviews
          conducted by third-party companies (<Strong>Research Buyers</Strong>).
        </P>
        <UL>
          <li>
            Pareto serves as a marketplace and payment intermediary. The Research Buyer designs
            the interview, sets the reward amount, and reviews your responses.
          </li>
          <li>
            You are paid a fixed reward per completed interview, as displayed at the time you
            accept the invitation.
          </li>
          <li>
            Payments are processed through <Strong>Stripe Connect</Strong>. You must complete
            Stripe Connect onboarding (including identity verification and tax information)
            before you can receive payouts.
          </li>
          <li>
            Payouts to your bank account or debit card are subject to Stripe's processing
            schedules and minimum amounts.
          </li>
          <li>
            <Strong>Tax obligations.</Strong> If you reside in the United States and earn $600
            or more from the Service in a calendar year, we will (where required) issue a Form
            1099-NEC and report your earnings to the IRS.{' '}
            <Strong>
              You are solely responsible for reporting all income earned and for paying any
              applicable taxes
            </Strong>
            , regardless of whether a 1099 is issued.
          </li>
          <li>
            We do not guarantee any minimum number of paid interview invitations or any
            minimum earning amount.
          </li>
          <li>
            If a Research Buyer determines your interview responses were low-quality,
            fraudulent, or inconsistent with the screener answers you provided, the reward may
            be reduced, withheld, or reversed.
          </li>
        </UL>
      </Section>

      <Section number={8} title="Communications and consent">
        <P>
          By providing your phone number or Apple ID and engaging with our WhatsApp or iMessage
          bot, you consent to receive:
        </P>
        <UL>
          <li>
            Operational messages: confirmations, interview invitations, application status
            updates, payment notifications.
          </li>
          <li>
            Match alerts: notifications about jobs and paid interviews we believe are a good
            fit for you.
          </li>
          <li>Occasional service announcements.</li>
        </UL>
        <P>
          Standard message and data rates may apply. You can opt out of non-essential messages
          at any time by replying "stop", "pause", or "settings" to the bot, or by updating
          notification preferences in the web dashboard.
        </P>
        <P>We do not send promotional messages for third-party products.</P>
      </Section>

      <Section number={9} title="Prohibited uses">
        <P>You agree not to:</P>
        <UL>
          <li>Create or maintain more than one account.</li>
          <li>
            Misrepresent your identity, work history, skills, location, or any other profile
            data.
          </li>
          <li>Use the Service to apply to jobs for someone other than yourself.</li>
          <li>
            Bypass, manipulate, or attempt to circumvent the daily application limits or any
            other rate-limiting or anti-abuse measures.
          </li>
          <li>
            Use the Service to send spam, harvest data, or scrape job listings or interview
            content.
          </li>
          <li>
            Reverse-engineer, decompile, or attempt to extract the source code of the Service,
            including any AI prompts, embeddings, or matching algorithms.
          </li>
          <li>Use the Service to commit fraud, identity theft, or any other illegal activity.</li>
          <li>
            Interfere with the operation of the Service, including by submitting automated
            traffic, exploiting vulnerabilities, or attempting denial-of-service attacks.
          </li>
          <li>
            Use the Service for any purpose that violates applicable law or the terms of any
            third-party platform (such as LinkedIn, Indeed, or an employer's career site).
          </li>
        </UL>
        <P>We may suspend or terminate your account immediately for any of the above.</P>
      </Section>

      <Section number={10} title="Intellectual property">
        <P>
          <Strong>Our content.</Strong> All software, branding, design, prompts, models, and
          other content of the Service are owned by Pareto Labs Inc or our licensors and are
          protected by intellectual-property laws. We grant you a limited, non-exclusive,
          non-transferable, revocable license to use the Service for its intended personal
          purpose. You receive no other rights.
        </P>
        <P>
          <Strong>Your content.</Strong> You retain ownership of the content you provide to
          us, including your resume, LinkedIn data, cover-letter inputs, and interview
          responses (<Strong>Your Content</Strong>). You grant Pareto a worldwide,
          non-exclusive, royalty-free license to use, store, copy, modify, transmit, and
          display Your Content as necessary to operate, improve, and provide the Service to
          you — including by passing it through third-party AI models and infrastructure
          providers as described in our Privacy Policy.
        </P>
        <P>
          We do not sell Your Content. We do not use Your Content to train general-purpose AI
          models. We may use anonymized, aggregated patterns derived from Your Content to
          improve matching quality.
        </P>
      </Section>

      <Section number={11} title="AI-generated content">
        <P>
          Resumes, cover letters, and interview prompts produced by the Service are generated
          by AI models. They may contain inaccuracies, omissions, or content that does not
          reflect your true experience. You are responsible for reviewing and, where you
          choose to do so, editing AI-generated content before it is submitted on your behalf.
          If auto-apply submits an AI-generated document automatically, you accept that the
          document may not be perfect and that any consequences (including a rejected
          application, an interview that you fail because the application overstated a
          qualification, or an employer disputing the application) are your responsibility,
          not ours.
        </P>
      </Section>

      <Section number={12} title="Termination">
        <P>
          You may delete your account at any time by emailing <Code>legal@trypareto.ai</Code>{' '}
          or using the in-app delete-account flow.
        </P>
        <P>
          We may suspend or terminate your account, with or without notice, if we believe you
          have violated these Terms, engaged in fraud, or used the Service in a way that
          exposes us or our users to harm or legal risk.
        </P>
        <P>On termination:</P>
        <UL>
          <li>Your auto-apply authorization is revoked.</li>
          <li>
            Any pending paid-interview rewards will be released to you if you completed the
            underlying interviews in good faith, subject to Stripe's payout schedule and any
            holds.
          </li>
          <li>
            We retain certain account information after termination as described in our
            Privacy Policy (including for legal, tax, and fraud-prevention reasons).
          </li>
        </UL>
      </Section>

      <Section number={13} title="Disclaimers">
        <P>
          The Service is provided "as is" and "as available," without warranties of any kind.
          To the maximum extent permitted by law, Pareto disclaims all warranties, express or
          implied, including warranties of merchantability, fitness for a particular purpose,
          accuracy, non-infringement, and uninterrupted operation.
        </P>
        <P>
          We do not guarantee that you will be matched to any specific job, hired by any
          employer, invited to any paid interview, or earn any particular amount through the
          Service.
        </P>
      </Section>

      <Section number={14} title="Limitation of liability">
        <P>
          To the maximum extent permitted by applicable law, in no event will Pareto, its
          officers, directors, employees, agents, or licensors be liable for any indirect,
          incidental, special, consequential, exemplary, or punitive damages — including,
          without limitation, lost profits, lost earnings, lost opportunities, lost data, or
          damages for emotional distress — arising out of or related to your use of the
          Service.
        </P>
        <P>
          Our total aggregate liability to you for any claim arising out of or related to the
          Service is limited to the greater of:
        </P>
        <UL>
          <li>one hundred U.S. dollars (USD&nbsp;$100), or</li>
          <li>
            the total amount of paid-interview rewards we paid you in the twelve (12) months
            immediately preceding the event giving rise to the claim.
          </li>
        </UL>
        <P>
          Some jurisdictions do not allow the exclusion or limitation of certain damages. To
          the extent these limitations are not enforceable in your jurisdiction, the
          limitations apply to the maximum extent permitted by law.
        </P>
      </Section>

      <Section number={15} title="Indemnification">
        <P>
          You agree to defend, indemnify, and hold harmless Pareto Labs Inc and its officers,
          directors, employees, and agents from and against any claims, liabilities, damages,
          losses, and expenses (including reasonable attorneys' fees) arising out of or
          related to:
        </P>
        <UL>
          <li>Your use of the Service.</li>
          <li>Your violation of these Terms.</li>
          <li>
            Your violation of any applicable law or third-party right (including any
            employer's terms or any third-party career site's terms).
          </li>
          <li>
            Any content you provide, including resume content and interview responses.
          </li>
        </UL>
      </Section>

      <Section number={16} title="Changes to these Terms">
        <P>
          We may update these Terms from time to time. If we make a material change, we will
          give you reasonable notice — typically by email and/or via a notice in the web
          dashboard and a message from the bot — before the change takes effect. Your continued
          use of the Service after the effective date of the change constitutes your
          acceptance of the updated Terms.
        </P>
        <P>
          The most current version of these Terms is always available at{' '}
          <Code>trypareto.ai/terms</Code>.
        </P>
      </Section>

      <Section number={17} title="Governing law and dispute resolution">
        <P>
          These Terms are governed by the laws of the <Strong>State of Delaware</Strong>,
          United States, without regard to its conflict-of-laws rules.
        </P>
        <P>
          Any dispute arising out of or related to these Terms or the Service that cannot be
          resolved through good-faith negotiation will be resolved by binding arbitration,
          conducted in English, in Wilmington, Delaware, under the rules of the American
          Arbitration Association. Each party will bear its own costs and fees, except that
          the arbitrator may award fees and costs to the prevailing party as permitted by law.
        </P>
        <P>
          You and Pareto each waive any right to a jury trial and to participate in a class
          action.
        </P>
      </Section>

      <Section number={18} title="Miscellaneous">
        <UL>
          <li>
            <Strong>Entire agreement.</Strong> These Terms, together with the Privacy Policy
            and any additional terms we present at the time of a specific feature, are the
            entire agreement between you and Pareto and supersede any prior agreement.
          </li>
          <li>
            <Strong>Severability.</Strong> If any provision of these Terms is held
            unenforceable, the remaining provisions remain in full force.
          </li>
          <li>
            <Strong>No waiver.</Strong> Our failure to enforce any provision is not a waiver
            of our right to enforce that provision later.
          </li>
          <li>
            <Strong>Assignment.</Strong> You may not assign or transfer these Terms without
            our prior written consent. We may assign these Terms in connection with a merger,
            acquisition, or sale of assets.
          </li>
        </UL>
      </Section>

      <Section number={19} title="Contact">
        <P>If you have questions about these Terms, you can reach us at:</P>
        <P>
          <Strong>Pareto Labs Inc</Strong>
          <br />
          Email: <Code>legal@trypareto.ai</Code>
        </P>
      </Section>
    </LegalLayout>
  );
}
